import { IComponentOptions, IScope } from "angular";
import { GtmhubController, RestLayerFilter } from "@gtmhub/core";
import { IEnrichedEmployee } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { Chartist, IPieChartOptions } from "@gtmhub/shared/libs/chartist/chartist.module";
import { IEnrichedTeam } from "@gtmhub/teams";
import { getAllTeamMembersIncludingManager, roundFloatAtMost2DecPlaces } from "@gtmhub/util";
import { IProcessStats, IProfileQueryParams, ProfileScreenName } from "../../models/models";
import { ProfileService } from "../../services/profileService";
import { IGenericCardStats, IStat } from "../generic-card/generic-card.component";
import { GroupDetailsSidebarConfig, GroupInfo } from "../group-details-sidebar/group-details-sidebar.component";
import { GroupDetailsSidebarService } from "../group-details-sidebar/group-details-sidebar.service";
import { ProfileFilterChangedEventArgs, ProfileSessionFilterEvents } from "../profile-session-filter/profile-session-filter.component";

interface IProcessSectionBindings {
  target: IEnrichedEmployee | IEnrichedTeam;
  indicators: {
    loading: boolean;
  };
}

export type ProcessGroup = "automatedKRs" | "manualKRs" | "ownershipKRs" | "ownershipOKRs";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ProcessSectionCtrl extends IProcessSectionBindings {}
export class ProcessSectionCtrl extends GtmhubController {
  avgUpdatesPerMetricStats: IGenericCardStats;
  metricsUpdatedOnTimeRateStats: IGenericCardStats;
  avgDesignScoreStats: IGenericCardStats;
  dynamicMetricsRateStats: IGenericCardStats;
  manualMetricsRateStatsMain: IStat;
  goalsOwnedCountStats: IGenericCardStats;
  metricsOwnedCountStats: IGenericCardStats;
  metricUpdatesCountStats: IGenericCardStats;
  screenName: ProfileScreenName;

  private sessionFilter: string;
  private processStats: Partial<IProcessStats>;
  private viewExists: boolean;
  private profileSessionFilterEvents: ProfileSessionFilterEvents;

  static $inject = ["ProfileService", "UIErrorHandlingService", "$scope", "GroupDetailsSidebarService"];

  constructor(
    private profileService: ProfileService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $scope: IScope,
    private groupDetailsSidebarService: GroupDetailsSidebarService
  ) {
    super();
  }

  $onInit(): void {
    this.screenName = this.target.type === "user" ? "employee profile" : "team profile";

    this.viewExists = true;
    this.profileSessionFilterEvents = new ProfileSessionFilterEvents(this.$scope);
    this.profileSessionFilterEvents.onFilterChanged((args: ProfileFilterChangedEventArgs) => this.onFilterChanged(args.selectedFilter));
  }

  $onDestroy(): void {
    this.viewExists = false;
  }

  public onFilterChanged(filterOption: string): void {
    this.indicators.loading = true;
    this.sessionFilter = filterOption;
    this.getProcessStats();
  }

  public viewDetails(group: ProcessGroup): void {
    const metricsGroup: GroupInfo = { type: "metrics", icon: "metric" };
    const goalsGroup: GroupInfo = { type: "goals", icon: "goal" };

    const configMap: Record<typeof group, GroupDetailsSidebarConfig> = {
      automatedKRs: {
        label: { icon: "metric", key: "automated_krs", infoKey: this.dynamicMetricsRateStats.main.value + this.dynamicMetricsRateStats.main.suffix },
        group: metricsGroup,
      },
      manualKRs: {
        label: { icon: "metric", key: "manual_krs", infoKey: this.manualMetricsRateStatsMain.value + this.manualMetricsRateStatsMain.suffix },
        group: metricsGroup,
      },
      ownershipKRs: {
        label: { icon: "metric", key: "ownership", infoKey: this.metricsOwnedCountStats.main.value + this.metricsOwnedCountStats.main.suffix },
        group: metricsGroup,
      },
      ownershipOKRs: {
        label: { icon: "goal", key: "ownership", infoKey: this.goalsOwnedCountStats.main.value + this.goalsOwnedCountStats.main.suffix },
        group: goalsGroup,
      },
    };

    const ownerIds = this.target.type === "user" ? [this.target.id] : [this.target.id, ...getAllTeamMembersIncludingManager(this.target)];

    const defaultFilers = [{ sessionId: { $in: [this.sessionFilter] } }, { ownerIds: { $in: ownerIds } }];

    const filterMap: Record<typeof group, RestLayerFilter> = {
      automatedKRs: {
        $and: [...defaultFilers, { insightName: { $exists: true } }, { insightName: { $ne: "" } }],
      },
      manualKRs: {
        $and: [...defaultFilers, { $or: [{ insightName: { $exists: false } }, { insightName: "" }] }],
      },
      ownershipKRs: {
        $and: [...defaultFilers],
      },
      ownershipOKRs: {
        $and: [...defaultFilers],
      },
    };

    this.groupDetailsSidebarService.open({ config: configMap[group], filter: filterMap[group], screenName: this.screenName });
  }

  private getProcessStats() {
    const queryParams: IProfileQueryParams = {
      sessionIds: this.sessionFilter,
    };

    this.profileService.getProcessStats(this.target.type, this.target.id, queryParams).then(
      (stats) => {
        this.indicators.loading = false;
        this.processStats = stats;
        this.setStats();
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        this.indicators.loading = false;
      }
    );
  }

  private setStats(): void {
    if (this.target.type === "user") {
      this.setAvgUpdatesPerMetricStats();
    } else {
      this.setMetricUpdatesCountStats();
    }

    this.setMetricsUpdatedOnTimeRateStats();
    this.setAvgDesignScoreStats();
    this.setDynamicMetricsRateStats();
    this.setGoalsOwnedCountStats();
    this.setMetricsOwnedCountStats();
  }

  private setMetricUpdatesCountStats(): void {
    const stats = this.processStats.metricUpdatesCount;

    this.metricUpdatesCountStats = {
      main: { value: roundFloatAtMost2DecPlaces(stats.teamMetricUpdatesCount), suffix: "" },
      company: { value: roundFloatAtMost2DecPlaces(stats.allMetricUpdatesCount), suffix: "" },
    };
  }

  private setAvgUpdatesPerMetricStats(): void {
    const stats = this.processStats.avgUpdatesPerMetric;

    this.avgUpdatesPerMetricStats = {
      main: { value: roundFloatAtMost2DecPlaces(stats.avgUpdatesForUserMetrics), suffix: "" },
      company: { value: roundFloatAtMost2DecPlaces(stats.avgUpdatesForAllMetrics), suffix: "" },
      team: { value: roundFloatAtMost2DecPlaces(stats.avgUpdatesForUserTeamsMetrics), suffix: "" },
    };
  }

  private setMetricsUpdatedOnTimeRateStats(): void {
    const stats = this.processStats.metricsUpdatedOnTimeRate;

    this.metricsUpdatedOnTimeRateStats = {
      main: {
        value: this.target.type === "user" ? roundFloatAtMost2DecPlaces(stats.userMetricsRate * 100) : roundFloatAtMost2DecPlaces(stats.teamMetricsRate * 100),
        suffix: "%",
      },
      company: { value: roundFloatAtMost2DecPlaces(stats.allMetricsRate * 100), suffix: "%" },
      team: { value: this.target.type === "user" ? roundFloatAtMost2DecPlaces(stats.userTeamsMetricsRate * 100) : null, suffix: "%" },
    };
  }

  private setAvgDesignScoreStats(): void {
    const stats = this.processStats.avgDesignScore;

    this.avgDesignScoreStats = {
      main: { value: this.target.type === "user" ? stats.avgDesignScoreForUserGoals : stats.avgDesignScoreForTeam, suffix: "/100 " },
      company: { value: stats.avgDesignScoreForAllGoals, suffix: "/100 " },
      team: { value: this.target.type === "user" ? stats.avgDesignScoreForUserTeamsGoals : null, suffix: "/100 " },
    };
  }

  private setDynamicMetricsRateStats(): void {
    const stats = this.processStats.dynamicMetricsRate;
    const suffix = "%";

    this.dynamicMetricsRateStats = {
      main: {
        value: this.target.type === "user" ? roundFloatAtMost2DecPlaces(stats.userMetricsRate * 100) : roundFloatAtMost2DecPlaces(stats.teamMetricsRate * 100),
        suffix,
      },
      company: { value: roundFloatAtMost2DecPlaces(stats.allMetricsRate * 100), suffix },
      team: { value: this.target.type === "user" ? roundFloatAtMost2DecPlaces(stats.userTeamsMetricsRate * 100) : null, suffix },
    };

    this.manualMetricsRateStatsMain = {
      value: roundFloatAtMost2DecPlaces(100 - this.dynamicMetricsRateStats.main.value),
      suffix,
    };
    if (this.viewExists) {
      // we need to know if the view is destroyed, otherwise execution continues,
      // chartist tries to find an element that no longer exists and type error occurs
      this.drawAutomatedVsManualPieChart(this.manualMetricsRateStatsMain.value, this.dynamicMetricsRateStats.main.value);
    }
  }

  private setGoalsOwnedCountStats(): void {
    const stats = this.processStats.goalsOwnedCount;
    const mainStats = this.target.type === "user" ? stats.ownedByUserCount : stats.ownedByTeamCount;
    const mainSuffix = mainStats === 1 ? localize("objective_cap") : localize("objectives_cap");

    this.goalsOwnedCountStats = {
      main: { value: mainStats, suffix: " " + mainSuffix },
      company: { value: stats.allGoalsCount, suffix: "" },
      team: { value: this.target.type === "user" ? stats.ownedByUserTeamsCount : null, suffix: "" },
    };
  }

  private setMetricsOwnedCountStats(): void {
    const stats = this.processStats.metricsOwnedCount;
    const mainStats = this.target.type === "user" ? stats.ownedByUserCount : stats.ownedByTeamCount;
    const mainSuffix = mainStats === 1 ? localize("key_result") : localize("key_results");

    this.metricsOwnedCountStats = {
      main: { value: mainStats, suffix: ` ${mainSuffix}` },
      company: { value: stats.allMetricsCount, suffix: "" },
      team: { value: this.target.type === "user" ? stats.ownedByUserTeamsCount : null, suffix: "" },
    };
  }

  private drawAutomatedVsManualPieChart = (manual: number, automated: number): void => {
    const getWhiteSpace = (value: number): string => {
      if (Math.floor(value) <= 3) {
        return "  ";
      } else if (Math.floor(value) <= 6) {
        return "    ";
      } else if (Math.floor(value) < 10) {
        return "      ";
      } else {
        return "";
      }
    };

    // values of each "piece" of the pie chart
    const data =
      manual === 0 || automated === 0
        ? {
            series: [100],
          }
        : {
            series: [manual, automated],
          };

    // containing width, height and label func of chart
    const options: IPieChartOptions = {
      width: 200,
      height: 200,
      labelOffset: (automated > 10 && automated < 12) || (automated > 86 && automated < 90) ? 15 : 0,
      labelInterpolationFnc: function (value: number) {
        return `${getWhiteSpace(value)}${value}%`;
      },
    };
    window.setTimeoutOutsideAngular(() => {
      const containerEl = document.querySelector(`.automated-vs-manual-chart.${this.target.type}`);
      if (!containerEl) {
        console.warn(`Cannot load pie chart: element '.automated-vs-manual-chart.${this.target.type}' does not exist in the DOM`);
      } else {
        new Chartist.Pie(containerEl, data, options);
      }
    });
  };
}

export const ProcessSectionComponent: IComponentOptions = {
  bindings: {
    indicators: "=",
    target: "<",
  },
  controller: ProcessSectionCtrl,
  template: require("./process-section.component.html"),
};
